<template>
  <div
    v-if="display === 'stack'"
    :class="{
      'avatar-stacked': true,
      'avatar-stacked-reversed': reversed,
      'avatar-stacked-sparse': sparse,
    }"
  >
    <Avatar
      v-for="(user, idx) in displayedUsers"
      :ref="`avatar${idx}`"
      :key="`avatar-stack-${idx}`"
      :size="avatarSize"
      :circle="circle"
      :src="user[selectorImage]"
      :name="user[selectorName]"
      :initial="auto && !!!user[selectorImage]"
      :tooltip-id="`avatar-stack-${idx}-${setKebabCase(user[selectorName])}`"
      :style="Number.isNaN(+avatarSize) ? null : spacingStyle(idx)"
    />

    <Avatar
      v-if="remaining >= 1"
      :ref="`avatarMore`"
      initial
      :filter-initial="false"
      :size="avatarSize"
      :circle="circle"
      :style="Number.isNaN(+avatarSize) ? null : spacingStyle(displayedUsers.length)"
      :name="`+${remaining.toString()}`"
    />
  </div>
  <div v-else>
    <div class="avatar-grid-container avatar-grid-small">
      <div
        v-for="(groups, i) in displayedUsers"
        :key="`avatar-grid-row-${i}`"
        class="avatar-grid-row"
      >
        <Avatar
          v-for="(user, j) in groups"
          :key="`avatar-grid-${j}`"
          :size="avatarSize"
          :circle="circle"
          :src="user[selectorImage]"
          :name="user[selectorName]"
          :initial="auto && !!!user[selectorImage]"
          :tooltip-id="`avatar-grid-${j}-${setKebabCase(user[selectorName])}`"
        />
      </div>
      <div
        v-if="remaining >= 1"
        class="avatar-grid-row"
      >
        <div class="avatar-grid-text avatar-stacked-text">
          <p>+{{ remaining }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// This component's style is handled by http://mekari-ui.sleekr.id/docs/1.0/components/avatar
// TODO: Add clickable button support

import Avatar from '../Avatar';
import { kebabCase } from 'lodash/string';

export default {
  name: 'AvatarGroup',
  components: {
    Avatar,
  },
  props: {
    /**
     * Display avatars as a grid
     */
    grid: {
      type: Boolean,
      default: false,
    },
    /**
     * Avatar's size
     */
    avatarSize: {
      type: [String, Number],
      default: 'medium',
    },
    /**
     * Display circle/rounded avatar
     */
    circle: {
      type: Boolean,
      default: true,
    },
    /**
     * Array of users. By default each item should be an object, contains `src`, and `name` attribute.
     * If not, specify the attribute's name on `selectorImage`, and/or `selectorName` properties.
     */
    users: {
      type: Array,
      default() {
        return [];
      },
    },
    /**
     * If in any case the users's avatar URL is listed as other attribute name, you can specify it here.
     */
    selectorImage: {
      type: String,
      default: 'src',
    },
    /**
     * If in any case the users's name/title is listed as other attribute name, you can specify it here.
     */
    selectorName: {
      type: String,
      default: 'name',
    },
    /**
     * When enabled, automatically convert invalid avatar to be an initial avatar instead.
     */
    auto: {
      type: Boolean,
      default: true,
    },
    /**
     * Number of Avatars that should be displayed within one line (also apply for grid)
     */
    size: {
      type: Number,
      default: 3,
      validator: value => value >= 1,
    },
    /**
     * Number of rows (only apply for grid)
     */
    rowSize: {
      type: Number,
      default: 1,
      validator: value => value >= 1,
    },
    /**
     * When enabled, first avatar will be below second avatar, second avatar will be below third avatar, and so on.
     * When disabled, first avatar will be above second avatar, second avatar will be above third avatar, and so on.
     */
    reversed: {
      type: Boolean,
      default: false,
    },
    /**
     * Add more space between each avatar
     */
    sparse: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    display() {
      if (this.grid) {
        return 'grid';
      }
      return 'stack';
    },
    displayedUsers() {
      if (this.display === 'stack') {
        return this.users.slice(0, this.size);
      }

      return this.split(
        this.users.slice(0, this.rowSize * this.size),
        this.size,
      );
    },
    remaining() {
      if (this.users && this.users.length >= 1) {
        if (this.display === 'stack') {
          return this.users.length - this.size;
        }

        const left = this.users.length - this.rowSize * this.size;
        return left >= 1 ? left : 0;
      }

      return 0;
    },
  },
  methods: {
    spacingStyle(index) {
      let marginLeft = 0;
      const marginLeftDivisor = this.sparse ? 8 : 4;
      if (index > 0) {
        marginLeft = -(this.avatarSize / marginLeftDivisor);
      }
      const zIndex = this.reversed ? index : this.displayedUsers.length - index;
      const boxShadowDistance = this.avatarSize / 40;
      const boxShadow = `${this.reversed ? -boxShadowDistance : boxShadowDistance}px 0 0 ${boxShadowDistance}px #fff`;
      return {
        'box-shadow': boxShadow,
        'margin-left': `${marginLeft}px`,
        'z-index': zIndex,
      };
    },
    split(arr, len) {
      const chunks = [];
      const n = arr.length;
      let i = 0;

      while (i < n) {
        chunks.push(arr.slice(i, i += len));
      }
      return chunks;
    },
    setKebabCase(string) {
      return kebabCase(string);
    },
  },
};
</script>
